import { useEffect, useRef, useState, type ReactNode } from "react";

type DeferredSectionProps = {
  /** Placeholder rendu tant que le composant n'est pas chargé (réserve la hauteur) */
  fallback: ReactNode;
  children: ReactNode;
  /** Marge de déclenchement avant l'entrée dans le viewport */
  rootMargin?: string;
};

/**
 * Monte ses enfants uniquement lorsque la section approche du viewport.
 * Combiné à React.lazy, cela fractionne le JS non critique et améliore le TTI.
 */
export function DeferredSection({
  fallback,
  children,
  rootMargin = "300px",
}: DeferredSectionProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (show) return;
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      setShow(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShow(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin, show]);

  return <div ref={ref}>{show ? children : fallback}</div>;
}
